import React from 'react';
import {
    View,
    Modal,
    StyleSheet,
    TouchableOpacity,
    Image,
    ScrollView,
    Linking,
    Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { H2, H3, Body1, Body2, Caption } from '../common/TextStyles';
import { getShadow } from '../../theme/useTheme';

const MentorProfileModal = ({ visible, mentor, onClose }) => {
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    if (!mentor) return null;

    const handleContactPress = (type) => {
        const { socialLinks, email, phone } = mentor;

        switch (type) {
            case 'email':
                if (email) {
                    Linking.openURL(`mailto:${email}`);
                }
                break;
            case 'phone':
                if (phone) {
                    Linking.openURL(`tel:${phone}`);
                }
                break;
            case 'instagram':
                if (socialLinks?.instagram) {
                    Linking.openURL(socialLinks.instagram);
                }
                break;
            case 'twitter':
                if (socialLinks?.twitter) {
                    Linking.openURL(socialLinks.twitter);
                }
                break;
            case 'linkedin':
                if (socialLinks?.linkedin) {
                    Linking.openURL(socialLinks.linkedin);
                }
                break;
        }
    };

    return (
        <Modal
            visible={visible}
            animationType="slide"
            transparent={true}
            statusBarTranslucent={true}
            onRequestClose={onClose}
        >
            <View style={styles.overlay}>
                <View style={styles.modalContainer}>
                    {/* Header with gradient */}
                    <LinearGradient
                        colors={['#6366F1', '#8B5CF6']}
                        style={styles.header}
                    >
                        <TouchableOpacity style={styles.closeButton} onPress={onClose}>
                            <Ionicons name="close" size={22} color="#FFFFFF" />
                        </TouchableOpacity>

                        <View style={styles.profileSection}>
                            <View style={styles.avatarContainer}>
                                {mentor.profileImageUrl ? (
                                    <Image
                                        source={{ uri: mentor.profileImageUrl }}
                                        style={styles.avatar}
                                    />
                                ) : (
                                    <View style={styles.avatarPlaceholder}>
                                        <Ionicons name="person" size={48} color="#FFFFFF" />
                                    </View>
                                )}
                                <View style={styles.badge}>
                                    <Ionicons name="star" size={12} color="#FFFFFF" />
                                </View>
                            </View>

                            <H2 style={styles.name}>{mentor.name}</H2>
                            <Caption style={styles.type}>{mentor.mentorType || 'Mentor'}</Caption>
                        </View>
                    </LinearGradient>

                    {/* Content */}
                    <ScrollView
                        style={styles.content}
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={{ paddingBottom: insets.bottom + 24 }}
                    >
                        {/* Bio Section */}
                        {mentor.mentorBio && (
                            <View style={styles.section}>
                                <View style={styles.sectionHeader}>
                                    <Ionicons name="document-text-outline" size={16} color="#6366F1" />
                                    <H3 style={styles.sectionTitle}>About</H3>
                                </View>
                                <Body2 style={styles.bio}>{mentor.mentorBio}</Body2>
                            </View>
                        )}

                        {/* Stats Section */}
                        {mentor.points !== undefined && (
                            <View style={styles.section}>
                                <View style={styles.sectionHeader}>
                                    <Ionicons name="trophy-outline" size={16} color="#6366F1" />
                                    <H3 style={styles.sectionTitle}>Impact</H3>
                                </View>
                                <View style={styles.statsRow}>
                                    <View style={styles.statCard}>
                                        <Caption style={styles.statLabel}>Points</Caption>
                                        <H3 style={styles.statValue}>{mentor.points || 0}</H3>
                                    </View>
                                    <View style={styles.statCard}>
                                        <Caption style={styles.statLabel}>Status</Caption>
                                        <Body2 style={styles.statValue}>Active</Body2>
                                    </View>
                                </View>
                            </View>
                        )}

                        {/* Contact Section */}
                        <View style={styles.section}>
                            <View style={styles.sectionHeader}>
                                <Ionicons name="chatbubbles-outline" size={16} color="#6366F1" />
                                <H3 style={styles.sectionTitle}>Get in Touch</H3>
                            </View>

                            <View style={styles.contactGrid}>
                                {mentor.email && (
                                    <TouchableOpacity
                                        style={styles.contactButton}
                                        onPress={() => handleContactPress('email')}
                                    >
                                        <View style={[styles.contactIcon, { backgroundColor: '#EF444415' }]}>
                                            <Ionicons name="mail" size={18} color="#EF4444" />
                                        </View>
                                        <Caption style={styles.contactLabel}>Email</Caption>
                                    </TouchableOpacity>
                                )}

                                {mentor.phone && (
                                    <TouchableOpacity
                                        style={styles.contactButton}
                                        onPress={() => handleContactPress('phone')}
                                    >
                                        <View style={[styles.contactIcon, { backgroundColor: '#10B98115' }]}>
                                            <Ionicons name="call" size={18} color="#10B981" />
                                        </View>
                                        <Caption style={styles.contactLabel}>Call</Caption>
                                    </TouchableOpacity>
                                )}

                                {mentor.socialLinks?.instagram && (
                                    <TouchableOpacity
                                        style={styles.contactButton}
                                        onPress={() => handleContactPress('instagram')}
                                    >
                                        <View style={[styles.contactIcon, { backgroundColor: '#EC489915' }]}>
                                            <Ionicons name="logo-instagram" size={18} color="#EC4899" />
                                        </View>
                                        <Caption style={styles.contactLabel}>Instagram</Caption>
                                    </TouchableOpacity>
                                )}

                                {mentor.socialLinks?.twitter && (
                                    <TouchableOpacity
                                        style={styles.contactButton}
                                        onPress={() => handleContactPress('twitter')}
                                    >
                                        <View style={[styles.contactIcon, { backgroundColor: '#3B82F615' }]}>
                                            <Ionicons name="logo-twitter" size={18} color="#3B82F6" />
                                        </View>
                                        <Caption style={styles.contactLabel}>Twitter</Caption>
                                    </TouchableOpacity>
                                )}

                                {mentor.socialLinks?.linkedin && (
                                    <TouchableOpacity
                                        style={styles.contactButton}
                                        onPress={() => handleContactPress('linkedin')}
                                    >
                                        <View style={[styles.contactIcon, { backgroundColor: '#0EA5E915' }]}>
                                            <Ionicons name="logo-linkedin" size={18} color="#0EA5E9" />
                                        </View>
                                        <Caption style={styles.contactLabel}>LinkedIn</Caption>
                                    </TouchableOpacity>
                                )}
                            </View>
                        </View>

                        <View style={styles.actionRowGrid}>
                            <TouchableOpacity
                                style={[styles.primaryButton, { flex: 1, marginBottom: 0 }]}
                                onPress={() => {
                                    onClose();
                                    navigation.navigate('MentorBooking', { mentor });
                                }}
                            >
                                <Ionicons name="calendar" size={16} color="#FFFFFF" />
                                <Body1 style={styles.primaryButtonText}>Book Session</Body1>
                            </TouchableOpacity>

                            <TouchableOpacity
                                style={[styles.secondaryButton, { flex: 1 }]}
                                onPress={() => handleContactPress('email')}
                            >
                                <Ionicons name="send" size={16} color="#6366F1" />
                                <Body1 style={[styles.primaryButtonText, { color: '#6366F1' }]}>Message</Body1>
                            </TouchableOpacity>
                        </View>

                        <Caption style={styles.versionTag}>Youth Connect • Mentor v1.5</Caption>
                    </ScrollView>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.6)',
        justifyContent: 'flex-end',
    },
    modalContainer: {
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        maxHeight: '92%',
        overflow: 'hidden',
    },
    header: {
        paddingTop: 14,
        paddingBottom: 22,
        paddingHorizontal: 18,
    },
    closeButton: {
        alignSelf: 'flex-end',
        padding: 3,
    },
    profileSection: {
        alignItems: 'center',
        marginTop: 4,
    },
    avatarContainer: {
        position: 'relative',
        marginBottom: 10,
    },
    avatar: {
        width: 100,
        height: 100,
        borderRadius: 50,
        borderWidth: 4,
        borderColor: 'rgba(255,255,255,0.3)',
    },
    avatarPlaceholder: {
        width: 76,
        height: 76,
        borderRadius: 38,
        backgroundColor: 'rgba(255,255,255,0.2)',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 3,
        borderColor: 'rgba(255,255,255,0.3)',
    },
    badge: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: 26,
        height: 26,
        borderRadius: 13,
        backgroundColor: '#F59E0B',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: '#FFFFFF',
    },
    name: {
        color: '#FFFFFF',
        fontSize: 19,
        fontWeight: '800',
        marginBottom: 2,
    },
    type: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 12,
        fontWeight: '600',
    },
    content: {
        padding: 18,
    },
    section: {
        marginBottom: 18,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginBottom: 8,
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: '#111827',
    },
    bio: {
        fontSize: 13,
        lineHeight: 19,
        color: '#4B5563',
    },
    statsRow: {
        flexDirection: 'row',
        gap: 10,
    },
    statCard: {
        flex: 1,
        backgroundColor: '#F9FAFB',
        borderRadius: 10,
        padding: 12,
        alignItems: 'center',
    },
    statLabel: {
        fontSize: 11,
        color: '#6B7280',
        marginBottom: 2,
    },
    statValue: {
        fontSize: 17,
        fontWeight: '700',
        color: '#111827',
    },
    contactGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    contactButton: {
        width: '30%',
        alignItems: 'center',
        gap: 5,
    },
    contactIcon: {
        width: 42,
        height: 42,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    contactLabel: {
        fontSize: 10,
        fontWeight: '600',
        color: '#6B7280',
    },
    primaryButton: {
        flexDirection: 'row',
        backgroundColor: '#6366F1',
        borderRadius: 12,
        padding: 13,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        ...getShadow('md'),
    },
    actionRowGrid: {
        flexDirection: 'row',
        gap: 12,
        marginTop: 4,
        marginBottom: 20,
    },
    secondaryButton: {
        flexDirection: 'row',
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        padding: 13,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        borderWidth: 1.5,
        borderColor: '#6366F1',
        ...getShadow('sm'),
    },
    primaryButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '700',
    },
    versionTag: {
        textAlign: 'center',
        opacity: 0.5,
        marginTop: 10,
        paddingBottom: 6,
    },
});

export default MentorProfileModal;
